import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template111: Tower Bridge & Torre de Londres
// Adaptado del componente Page8.jsx de guia-londres para usar las props del sistema guia
const Template111 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("portada");
	const torreSticker = getCanvasImageUrl("torre-sticker");
	const torreSticker2 = getCanvasImageUrl("torre-sticker-2");

	const introText = page.texts?.[1]?.content;
	const subtitle = page.texts?.[2]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const reserva =
		page?.reserva ?? page?.data?.reserva ?? page?.data?.data?.reserva;
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;

	// Lógica de color del header
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	let actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				<img
					src={portadaImage}
					alt="Londres"
					style={{ width: "100%", height: 200, objectFit: "cover" }}
				/>
				{subtitle && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: isSmallScreen ? "24px" : "30px",
							textAlign: isSmallScreen ? "left" : "center",
							position: "absolute",
							bottom: "0",
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{subtitle}
					</Box>
				)}
				<img
					src={torreSticker}
					alt="Tower Bridge"
					style={{
						width: isSmallScreen ? 90 : 108,
						position: "absolute",
						bottom: isSmallScreen ? 85 : 60,
						left: 10,
					}}
				/>
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
					marginTop: "0px !important",
					paddingTop: "4px !important",
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginTop: "-10px",
							paddingTop: "0px",
							padding: 0,
						}}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "12px",
						marginTop: "0px",
						paddingTop: "0px",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc =
							section.layout === "box"
								? getCanvasImageUrl(section.imageId)
								: getImageSrc(section.imageId);
						if (section.layout === "box") {
							return (
								<Box key={idx} data-block-id={`text-${idx + 2}`}>
									<Box
										sx={{ display: "flex", gap: "16px", position: "relative" }}
									>
										<Box sx={{ width: "100%" }}>
											<Box
												component="p"
												sx={{
													...estiloP,
													padding: "12px",
													backgroundColor: "#f4e4df",
													borderRadius: "12px",
												}}
											>
												{imageSrc && (
													<img
														src={imageSrc}
														alt={section.title}
														style={{
															width: isSmallScreen ? 105 : 115,
															float: "right",
															marginRight: -34,
															marginTop: -25,
														}}
													/>
												)}
												{section.title && (
													<Box component="h3" sx={estiloH3}>
														{section.title}
													</Box>
												)}
												{section.body}
											</Box>
										</Box>
									</Box>
									{idx === 1 && reserva && (
										<>
											<Box
												sx={{
													position: "absolute",
													bottom: isSmallScreen ? 100 : 70,
													left: isSmallScreen ? 60 : 4,
													backgroundColor: "#fff8db",
													border: "2px solid #f5c400",
													borderRadius: "16px 16px 16px 0px",
													padding: "10px 12px",
													fontSize: 11,
													fontFamily: `${
														fontFamily || "'Poppins', sans-serif"
													}`,
													color: contentColor,
													boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
													maxWidth: isSmallScreen ? 240 : 120,
													zIndex: 6,
												}}
											>
												<a
													href={reserva.url}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: "#000000",
														fontWeight: 700,
														textDecoration: "underline",
													}}
												>
													{reserva.text}
												</a>{" "}
												{reserva.suffix}
											</Box>
											{torreSticker2 && (
												<img
													src={torreSticker2}
													alt="Torre Londres"
													style={{
														width: 100,
														position: "absolute",
														bottom: isSmallScreen ? 70 : 0,
														left: -20,
														zIndex: 5,
													}}
												/>
											)}
										</>
									)}
								</Box>
							);
						}
						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{ display: "flex", gap: "16px", position: "relative" }}
							>
								{imageSrc && (
									<img
										src={imageSrc}
										alt={section.title}
										style={{
											width: "40%",
											height: 110,
											objectFit: "cover",
											borderRadius: "12px",
										}}
									/>
								)}
								{section.sabiasQue &&
									section.sabiasQue.map((sq, i) => (
										<Box
											key={i}
											sx={{
												position: "absolute",
												top: i === 0 ? -10 : 30 + (i - 1) * 2,
												left: "32%",
											}}
										>
											<SabiasQue icono={sq.icono} texto={sq.texto} />
										</Box>
									))}
								<Box sx={{ width: "60%" }}>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box
											component="p"
											sx={{ ...estiloP, fontSize: 13, color: contentColor }}
										>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}

					{llegar && llegar.title && (
						<Box
							data-block-id="text-6"
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								marginTop: isSmallScreen ? "90px" : "0",
							}}
						>
							<Box
								sx={{
									backgroundColor: "#f4e4df",
									border: "3px solid #ec7447",
									borderRadius: "12px",
									padding: "8px 20px",
									fontSize: 12,
									fontFamily: `${
										fontFamily || "'Poppins', sans-serif"
									} !important`,
									color: "#333",
									marginBottom: isSmallScreen ? "12px" : 0,
									width: isSmallScreen ? "100%" : "80%",
								}}
							>
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: "#de5624",
										textTransform: "uppercase",
									}}
								>
									{llegar.title}
								</Box>
								{llegar.content && (
									<Box
										component="div"
										dangerouslySetInnerHTML={{ __html: llegar.content }}
										sx={{
											paddingLeft: "0px",
											margin: 0,
											lineHeight: 1.5,
											"& ul": { paddingLeft: 4, margin: 0 },
										}}
									/>
								)}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template111.thumbnail = "/images/template-1.png";

export default Template111;
